/**
 * UserCreate Page - Create a new user
 *
 * Requirements: 2.2
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '@shared/components';
import { usePermissions } from '@shared/auth';
import { UserForm } from '../components';
import { createUser, listUsers } from '../api';
import type { CreateUserRequest } from '../api';
import { useAdminContext } from '../AdminPage';
import styles from './UserCreate.module.css';

export const UserCreate: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessToken } = usePermissions();
  const { refreshUserList } = useAdminContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (data: CreateUserRequest) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const token = await getAccessToken();
        const result = await createUser(token, data);

        if (result.success) {
          const newUserId = result.data.id;
          navigate('/admin');

          // Retry mechanism to wait for the user to appear in the list
          const checkForNewUser = async (attempts = 0) => {
            if (attempts >= 10) { // Max 10 attempts (5 seconds)
              refreshUserList(); // Final refresh attempt
              return;
            }

            try {
              const token = await getAccessToken();
              const listResult = await listUsers(token);

              if (listResult.success) {
                const userExists = listResult.data.some(user => user.id === newUserId);

                if (userExists) {
                  refreshUserList();
                } else {
                  // Wait 500ms and try again
                  setTimeout(() => checkForNewUser(attempts + 1), 500);
                }
              }
            } catch (err) {
              setTimeout(() => checkForNewUser(attempts + 1), 500);
            }
          };

          // Start checking after a short delay
          setTimeout(() => checkForNewUser(), 500);
        } else {
          setError(result.error.message);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create user');
      } finally {
        setIsSubmitting(false);
      }
    },
    [getAccessToken, navigate]
  );

  return (
    <div className={styles.container}>
      <AppHeader currentApp="admin" />
      <main className={styles.main}>
        <div className={styles.headerSection}>
          <h1 className={styles.title}>Create User</h1>
          <p className={styles.subtitle}>
            Add a new user and assign their application access roles.
          </p>
        </div>
        <UserForm
          mode="create"
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={error}
        />
      </main>
    </div>
  );
};
